import PropTypes from 'prop-types';
import React from 'react';

import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';

import {ROLES} from './Menu';
import {OptionContentType} from './MenuConstants';
import OptionContainer, {OptionContainerProps} from './OptionContainer';
import OptionContent, {OptionContentProps} from './OptionContent';

export type OptionProps<T> = OptionContentProps & OptionContainerProps<T>;

/**
 * This Option component is used for Menu options and supports icons and metadata.
 *
 * Renders OptionContainer and OptionContent, which can be used instead for components like SearchDropdown
 * that want to handle interactions and presentational content separately.
 *
 * To make an Option which copies text to the clipboard on select, use the `onSelect` prop and the
 * `copyToClipboard` function from util/copyToClipboard. *Do not wrap the Option children with <Clipboard>
 * as the Clipboard will not take the entire space of the Option, so clicking on the edges of the Option will
 * not properly copy to clipboard. Example: proteins/alignments/ProteinAlignmentToolbar.js:138.
 *
 * See /_design/dropdowns to see example usages.
 */
// eslint-disable-next-line react/prefer-stateless-function
class Option<T> extends React.Component<OptionProps<T>> {
  static propTypes = {
    // The content of the option.
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
    className: PropTypes.string,
    download: PropTypes.string,
    // If specified, then this option acts as a link pointed to this URL.
    href: PropTypes.string,
    openInNewTab: PropTypes.bool,
    contentType: PropTypes.oneOf(Object.values(OptionContentType)),
    // `icon` is rendered as <BenchlingIcon icon={icon} /> in the option.
    icon: PropTypes.string,
    iconClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    childrenClassName: PropTypes.string,
    tooltipTitle: PropTypes.string,
    tooltipPlacement: PropTypes.string,
    isDisabled: PropTypes.bool,
    // If a value is not passed, it defaults to mouse hovering
    isHovered: PropTypes.bool,
    // Whether the option is selected.
    isSelected: PropTypes.bool,
    onHover: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseUp: PropTypes.func,
    // Handler for selection, called with (value)
    onSelect: PropTypes.func,
    // Rendered under the main text.
    metadata: PropTypes.node,
    shouldEllipsize: PropTypes.bool.isRequired,
    hasBorder: PropTypes.bool,
    heightOverride: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types

    // Do not pass in `submenu` -- it will be passed in automatically.
    _submenu: PropTypes.node,
    // Do not define `_role` -- this is used internally by the component.
    _role: PropTypes.oneOf(Object.values(ROLES)).isRequired,
    // Disables bubbling up events to parent elements on selection.
    disableEventPropagation: PropTypes.bool,
    dataTestAttrs: DataTestAttrsType,
  };

  static defaultProps = {
    _role: ROLES.OPTION,
    contentType: OptionContentType.ANCHOR,
    shouldEllipsize: false,
    disableEventPropagation: false,
  };

  override render(): JSX.Element {
    return (
      <OptionContainer
        className={this.props.className}
        linkClassName={this.props.linkClassName}
        download={this.props.download}
        href={this.props.href}
        openInNewTab={this.props.openInNewTab}
        contentType={this.props.contentType}
        isDisabled={this.props.isDisabled}
        isHovered={this.props.isHovered}
        isSelected={this.props.isSelected}
        onHover={this.props.onHover}
        onMouseOut={this.props.onMouseOut}
        onMouseUp={this.props.onMouseUp}
        onSelect={this.props.onSelect}
        shouldEllipsize={this.props.shouldEllipsize}
        hasBorder={this.props.hasBorder}
        heightOverride={this.props.heightOverride}
        value={this.props.value}
        _submenu={this.props._submenu}
        disableEventPropagation={this.props.disableEventPropagation}
      >
        <OptionContent
          icon={this.props.icon}
          iconClassName={this.props.iconClassName}
          className={this.props.contentClassName}
          childrenClassName={this.props.childrenClassName}
          metadata={this.props.metadata}
          tooltipTitle={this.props.tooltipTitle}
          tooltipPlacement={this.props.tooltipPlacement}
          hasSubmenu={Boolean(this.props._submenu)}
          dataTestAttrs={this.props.dataTestAttrs}
          titleAttr={
            // The `title` attribute is presented as a tooltip, and is only useful when the menu item may
            // be ellipsized
            this.props.shouldEllipsize && typeof this.props.children === 'string'
              ? this.props.children
              : undefined
          }
        >
          {this.props.children}
        </OptionContent>
      </OptionContainer>
    );
  }
}

export default Option;
